import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { formatDate } from 'helpers';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import Locales from 'locales';
import { useAPI } from 'hooks';
import { PreferencesContext } from 'context';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import { Button } from 'components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'components/ui/select';
import PreferencesForm from './components/PreferencesForm';
import AddCampaignModal from './components/AddCampaignModal';

import reloadBrowserPage from './reloadBrowserPage';
const SuperAdminPreferences = () => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [selectYear, setSelectedYear] = useState('');
  const [pendingYear, setPendingYear] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [showConfirmChange, setShowConfirmChange] = useState(false);

  const { data: campaigns } = useQuery({
    queryFn: api.list('campaign/'),
    queryKey: ['campaign/'],
  });

  const {
    preferences,
    registrationDeposit,
    religiousSupervisorsQuota,
    medicalSupervisorsQuota,
    campaignYear,
    minPilgrimForAuthorization,
    pilgrimEligibilityMinAge,
    campaignRegistrationEndDate,
    totalPilgrimQuota,
    religiousSupervisorsRatio,
    medicalSupervisorsRatio,
  } = useContext(PreferencesContext);

  const form = useForm({
    defaultValues: {
      campaign_year: campaignYear || currentYear,
      pilgrim_eligibility_min_age: pilgrimEligibilityMinAge,
      registration_deposit: registrationDeposit,
      certificate_min_active_pilgrim: minPilgrimForAuthorization,
      campaign_registration_end_date: campaignRegistrationEndDate,
      religious_supervisors_quota: religiousSupervisorsQuota,
      medical_supervisors_quota: medicalSupervisorsQuota,
      religious_supervisors_ratio: religiousSupervisorsRatio,
      medical_supervisors_ratio: medicalSupervisorsRatio,
      total_pilgrim_quota: totalPilgrimQuota,
    },
  });

  useEffect(() => {
    if (!preferences) return;

    form.reset({
      campaign_year: campaignYear,
      pilgrim_eligibility_min_age: pilgrimEligibilityMinAge,
      registration_deposit: registrationDeposit,
      certificate_min_active_pilgrim: minPilgrimForAuthorization,
      campaign_registration_end_date: campaignRegistrationEndDate ? new Date(campaignRegistrationEndDate) : null,
      religious_supervisors_quota: religiousSupervisorsQuota,
      medical_supervisors_quota: medicalSupervisorsQuota,
      religious_supervisors_ratio: religiousSupervisorsRatio,
      medical_supervisors_ratio: medicalSupervisorsRatio,
      total_pilgrim_quota: totalPilgrimQuota,
    });

    setSelectedYear(String(campaignYear));
  }, [preferences, campaignYear]);

  const { mutate: editPreference, isLoading } = useMutation({
    mutationFn: ({ data }) => api.update('preference', data),
    onSuccess: (_, { reloadOnSuccess }) => {
      queryClient.invalidateQueries({ queryKey: ['preference/current/'] });
      queryClient.invalidateQueries({ queryKey: ['permission/current/'] });
      queryClient.invalidateQueries({ queryKey: ['campaign/'] });
      toast.success(Locales.preferences.toast.success);
      if (reloadOnSuccess) reloadBrowserPage();
    },
  });

  const savePreferences = (formData, reloadOnSuccess) => {
    const data = {
      ...formData,
      campaign_registration_end_date: formatDate(formData.campaign_registration_end_date, 'yyyy-MM-dd'),
    };
    editPreference({ data, reloadOnSuccess });
  };

  const submitForm = (formData) => {
    savePreferences({ ...formData, campaign_year: campaignYear }, false);
  };

  const submitNewCampaign = (formData) => {
    savePreferences(formData, false);
  };

  const handleYearChangeAttempt = (year) => {
    setPendingYear(year);
    setShowConfirmChange(true);
  };

  const confirmYearChange = () => {
    setSelectedYear(pendingYear);
    setShowConfirmChange(false);
    savePreferences({ ...form.getValues(), campaign_year: Number(pendingYear) }, true);
  };

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  return (
    <section className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-white flex items-start justify-center py-10">
      <div className="max-w-5xl w-full mx-auto space-y-6 px-6">
        {(!preferences || !campaigns) && (
          <EmptyCard className="bg-white rounded-sm border border-solid border-gray-200" />
        )}

        {preferences && campaigns && (
          <>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold">{Locales.preferences.title}</h2>

                <Select value={selectYear} onValueChange={handleYearChangeAttempt}>
                  <SelectTrigger className="w-[100px] rounded-lg border-[#d7ded9] focus:ring-[#287c50]/20">
                    <SelectValue placeholder="Campagne" />
                  </SelectTrigger>

                  <SelectContent>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.year} value={String(campaign.year)}>
                        {campaign.year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button size="sm" variant="outline" className="border-[#d7ded9] shadow-none" onClick={handleOpenAddModal}>
                <Plus />
                Nouvelle campagne
              </Button>
            </div>

            <div className="border-t my-4" />

            <PreferencesForm form={form} onSubmit={submitForm} currentYear={currentYear} isLoading={isLoading} />

            <ConfirmationModal
              isOpen={showConfirmChange}
              setOpenModal={setShowConfirmChange}
              title="Changement de campagne"
              message={`Passer à la campagne ${pendingYear} ? Les données affichées seront réinitialisées pour l'année ${pendingYear}. Souhaitez-vous continuer ?`}
              onConfirm={confirmYearChange}
              confirmBtnLabel="Confirmer"
            />

            {openAddModal && (
              <AddCampaignModal
                isOpened={openAddModal}
                campaigns={campaigns}
                onSubmit={submitNewCampaign}
                currentYear={currentYear}
                isLoading={isLoading}
                closeModal={() => setOpenAddModal(false)}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default SuperAdminPreferences;
